import AutoSizer from 'react-virtualized-auto-sizer';
import ForwardIcon from '@mui/icons-material/ArrowForward';
import Globe from '@apps-common/react-globe';
import Link from './Link.jsx';
import PropTypes from 'prop-types';
import React, {useMemo} from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  Box,
  Grid,
  TableCell,
  TableHead,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import {FixedSizeList as List} from 'react-window';
import {formatDate} from '../util/date.js';
import {getCenterForGeometry} from '../util/map.js';
import {useTheme} from '@mui/material/styles';

dayjs.extend(relativeTime);

/**
 * @param {string} dateString A formatted date string.
 * @returns {string} A relative time string.
 */
function formatDistanceToNow(dateString) {
  return dayjs(dateString).fromNow();
}

const COLUMNS = [
  {
    title: '',
    className: 'globe-column',
    width: 1,
  },
  {
    title: 'Subscription name',
    className: 'subscription-name-column',
    width: 4,
  },
  {
    title: 'Start',
    className: 'start-date-column',
    width: 2,
    align: 'right',
  },
  {
    title: 'End',
    className: 'end-date-column',
    width: 2,
    align: 'right',
  },
  {
    title: 'Created',
    className: 'created-column',
    width: 2,
    align: 'right',
  },
  {
    title: '',
    className: 'controls',
    width: 1,
    align: 'right',
  },
];

const GLOBE_SIZE = 48;

const GlobeTween = styled(Box)(({theme}) => ({
  backgroundColor: theme.palette.grey[100], //"#ffffff",
  borderRadius: GLOBE_SIZE / 2,
  width: GLOBE_SIZE,
  height: GLOBE_SIZE,
  display: 'inline-block',
}));

const PaddedItem = styled(Grid)(({theme}) => ({
  padding: theme.spacing(2),
  paddingRight: theme.spacing(1),
}));

PaddedItem.defaultProps = {
  item: true,
};

const HoverGrid = styled(Grid)(({theme}) => ({
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
    cursor: 'pointer',
  },
  '&:hover .controls': {
    visibility: 'unset',
  },
  '& .controls': {
    visibility: 'hidden',
  },
}));

const ForwardButton = styled(ForwardIcon)(({theme}) => ({
  color: theme.palette.primary.main,
  borderStyle: 'solid',
  borderWidth: 1,
  borderColor: theme.palette.grey[500],
  borderRadius: 4,
  padding: 2,
  width: 20,
  height: 20,
}));

function SubscriptionListItem({style, isScrolling, data, index}) {
  const subscription = data[index];
  const theme = useTheme();

  return (
    <Link to={`/subscription/${subscription.id}/`} underline="none">
      <HoverGrid container style={style}>
        <Grid
          item
          align="center"
          xs={COLUMNS[0].width}
          sx={{display: 'flex', alignItems: 'center'}}
        >
          <Box paddingLeft={1}>
            {isScrolling && <GlobeTween />}
            {!isScrolling && (
              <Globe
                center={getCenterForGeometry(subscription.geometry)}
                markerColor="#00A0B4"
                landColor={theme.palette.grey[500]}
                waterColor="#fff"
                diameter={GLOBE_SIZE}
              />
            )}
          </Box>
        </Grid>

        <PaddedItem xs={COLUMNS[1].width}>
          <Tooltip title={subscription.title}>
            <Typography
              component="div"
              variant="body2"
              noWrap
              sx={{fontWeight: 'bold'}}
            >
              {subscription.title}
            </Typography>
          </Tooltip>
          <Typography component="div" variant="body2" noWrap>
            {subscription.description}
          </Typography>
        </PaddedItem>

        <PaddedItem xs={COLUMNS[2].width} align="right">
          {formatDate(new Date(subscription.startTime))}
        </PaddedItem>

        <PaddedItem xs={COLUMNS[3].width} align="right">
          {formatDate(new Date(subscription.endTime))}
        </PaddedItem>

        <PaddedItem xs={COLUMNS[4].width} align="right">
          {formatDistanceToNow(subscription.created)}
        </PaddedItem>

        <PaddedItem xs={COLUMNS[5].width} align="right">
          <Box
            className="controls"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <ForwardButton />
          </Box>
        </PaddedItem>
      </HoverGrid>
    </Link>
  );
}

SubscriptionListItem.propTypes = {
  isScrolling: PropTypes.bool,
  style: PropTypes.object,
  data: PropTypes.array,
  index: PropTypes.number,
};

const createFilterFunction = filter => {
  const invert = filter.substring(0, 1) === '!';
  const fstr = (invert ? filter.substring(1) : filter).toLowerCase();

  if (!fstr) {
    return () => true;
  }

  return sub => {
    const match =
      sub.description.toLowerCase().indexOf(fstr) >= 0 ||
      sub.title.toLowerCase().indexOf(fstr) >= 0;
    return (invert && !match) || (!invert && match);
  };
};

const createFilter = filter => {
  if (!filter) {
    return () => true;
  }

  const filterFuncs = filter
    .replace(/ +/g, ' ')
    .split(' ')
    .map(word => createFilterFunction(word));
  const len = filterFuncs.length;

  return sub => {
    for (let i = 0; i < len; i++) {
      if (!filterFuncs[i](sub)) {
        return false;
      }
    }
    return true;
  };
};

function SubscriptionList({subscriptions, feeds, filter}) {
  const matchFunc = useMemo(() => createFilter(filter), [filter]);

  const subsWithFeeds = useMemo(() => {
    const feedsMap = {};
    for (let i = 0, ii = feeds.length; i < ii; i++) {
      const id = feeds[i].id;
      feedsMap[id] = i;
    }

    const subsWithFeeds = [];
    for (let i = 0, ii = subscriptions.length; i < ii; i++) {
      const subscription = subscriptions[i];
      if (matchFunc(subscription)) {
        subsWithFeeds.push({
          ...subscription,
          feed: feeds[feedsMap[subscription.feedID]],
        });
      }
    }

    return subsWithFeeds;
  }, [subscriptions, feeds, matchFunc]);

  return (
    <Box flex={1} display="flex" flexDirection="column">
      <TableHead component="div">
        <Grid container>
          {COLUMNS.map(column => (
            <Grid
              item
              xs={column.width}
              key={column.className}
              className={column.className}
            >
              <TableCell
                component="div"
                sx={{
                  textAlign: column.align,
                  display: 'block',
                  width: '100%',
                  height: '100%',
                }}
              >
                {column.title}
              </TableCell>
            </Grid>
          ))}
        </Grid>
      </TableHead>
      <Box flex={1}>
        <AutoSizer>
          {({height, width}) => (
            <List
              itemData={subsWithFeeds}
              width={width}
              height={height}
              itemCount={subsWithFeeds.length}
              itemSize={68}
              useIsScrolling
            >
              {SubscriptionListItem}
            </List>
          )}
        </AutoSizer>
      </Box>
    </Box>
  );
}

SubscriptionList.propTypes = {
  subscriptions: PropTypes.array,
  feeds: PropTypes.array,
  filter: PropTypes.string,
};

export default SubscriptionList;
